import { Box, Card, CardContent, Typography, Stack, Chip, Divider, Tooltip, Button, Grid, Container, CircularProgress, Alert } from "@mui/material";
import { CheckCircle, FolderSpecial, FolderOff, TrendingUp, ArrowForward, Add, History, Article, Settings, ErrorOutline } from "@mui/icons-material";
import { useDashboard } from "../hooks/useDashboard";
import { useHistory } from "../hooks/useHistory";
import { useLogs } from "../hooks/useLogs";
import { useSettings } from "../hooks/useSettings";
import { useNavigate } from "react-router";
import { routes } from "../routes";
import { useTranslation } from "react-i18next";

const DASHBOARD_TODAY_START = new Date(new Date().setHours(0, 0, 0, 0));

const ACTION_TYPE_COLORS: Record<string, { bg: string; color: string }> = {
    Move: { bg: 'rgba(37,99,235,0.1)', color: '#2563EB' },
    Copy: { bg: 'rgba(14,165,233,0.1)', color: '#0EA5E9' },
    Delete: { bg: 'rgba(239,68,68,0.1)', color: '#EF4444' },
    Rename: { bg: 'rgba(139,92,246,0.1)', color: '#8B5CF6' },
};

function statusIsActive(status: unknown, enabledFallback?: boolean): boolean {
    if (typeof status === "boolean") return status;
    if (typeof status === "number") return status === 1;
    if (status == null && typeof enabledFallback === "boolean") return enabledFallback;
    return String(status).toLowerCase() === "active";
}

function formatDate(iso?: string | Date, locale?: string) {
    if (!iso) return "";
    const d = new Date(iso as string);
    return isNaN(d.getTime()) ? "" : d.toLocaleDateString(locale ?? "es-ES", { day: "2-digit", month: "short", year: "numeric" });
}

function formatTime(iso?: string | Date, locale?: string) {
    if (!iso) return "";
    const d = new Date(iso as string);
    return isNaN(d.getTime()) ? "" : d.toLocaleTimeString(locale ?? "es-ES", { hour: "2-digit", minute: "2-digit" });
}

interface KpiCardProps {
    label: string;
    value: number;
    description: string;
    icon: React.ReactNode;
    accentColor: string;
    iconBg: string;
    iconColor: string;
    valueColor?: string;
}

function KpiCard({ label, value, description, icon, accentColor, iconBg, iconColor, valueColor }: KpiCardProps) {
    return (
        <Card sx={{
            height: '100%',
            border: '1px solid #E2E8F0',
            borderTop: `3px solid ${accentColor}`,
            boxShadow: 'none',
            transition: 'box-shadow 0.2s, transform 0.2s',
            '&:hover': { boxShadow: '0 4px 20px rgba(0,0,0,0.08)', transform: 'translateY(-2px)' },
        }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
                    <Typography variant="body2" sx={{ color: '#64748B', fontWeight: 500, fontSize: '0.8rem' }}>
                        {label}
                    </Typography>
                    <Box sx={{ bgcolor: iconBg, color: iconColor, width: 34, height: 34, borderRadius: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        {icon}
                    </Box>
                </Stack>
                <Typography variant="h4" fontWeight={700} sx={{ color: valueColor ?? '#0F172A', lineHeight: 1, mb: 0.5 }}>
                    {value}
                </Typography>
                <Typography variant="caption" sx={{ color: '#94A3B8', fontSize: '0.72rem' }}>
                    {description}
                </Typography>
            </CardContent>
        </Card>
    );
}

export default function DashboardPage() {
    const { t, i18n } = useTranslation();
    const { data, isLoading, error } = useDashboard();
    const { data: history } = useHistory({ count: 100 });
    const { data: errorLogs } = useLogs({ level: 'Error', count: 100, since: DASHBOARD_TODAY_START });
    const { data: settings } = useSettings();
    const navigate = useNavigate();

    const locale = i18n.language === 'en' ? 'en-US' : 'es-ES';

    function formatRelativeTime(iso?: string): string {
        if (!iso) return "";
        const diff = Date.now() - new Date(iso).getTime();
        const mins = Math.floor(diff / 60000);
        if (mins < 1) return t('dashboard.justNow');
        if (mins < 60) return t('dashboard.minutesAgo', { mins });
        const hours = Math.floor(mins / 60);
        if (hours < 24) return t('dashboard.hoursAgo', { hours });
        return formatDate(iso, locale);
    }

    const lastRunByRuleId = new Map<string, string>();
    (history ?? []).forEach(h => {
        if (h.ruleId && h.timestamp && !lastRunByRuleId.has(h.ruleId)) {
            lastRunByRuleId.set(h.ruleId, h.timestamp);
        }
    });

    const errorsToday = errorLogs?.length ?? 0;
    const recentActivity = (history ?? []).slice(0, 6);
    const lastExecution = history?.[0]?.timestamp;

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <CircularProgress />
            </Box>
        );
    }
    if (error) {
        return (
            <Container maxWidth="xl" sx={{ pt: 4 }}>
                <Alert severity="error">{t('dashboard.loadError')}</Alert>
            </Container>
        );
    }

    const stats = data?.ruleStats;
    const latest = (data?.latestRules ?? []).slice(0, 6);
    const activePct = stats?.total ? Math.round(((stats.active ?? 0) / stats.total) * 100) : 0;

    return (
        <Box sx={{ height: '100%', bgcolor: '#F8FAFC', overflow: 'hidden' }}>
            <Container maxWidth="xl" sx={{ pt: 2, height: '100%', boxSizing: 'border-box' }}>
                {/* KPIs */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(5, 1fr)' }, gap: 2, mb: 2 }}>
                    <KpiCard
                        label={t('dashboard.kpi.totalRules')} value={stats?.total ?? 0} description={t('dashboard.kpi.rulesConfigured')}
                        icon={<FolderSpecial sx={{ fontSize: 18 }} />} accentColor="#2563EB"
                        iconBg="rgba(37,99,235,0.1)" iconColor="#2563EB"
                    />
                    <KpiCard
                        label={t('dashboard.kpi.active')} value={stats?.active ?? 0}
                        description={stats?.total ? t('dashboard.kpi.pctOfTotal', { pct: activePct }) : t('dashboard.kpi.noRulesYet')}
                        icon={<CheckCircle sx={{ fontSize: 18 }} />} accentColor="#10B981"
                        iconBg="rgba(16,185,129,0.1)" iconColor="#10B981" valueColor="#10B981"
                    />
                    <KpiCard
                        label={t('dashboard.kpi.inactive')} value={stats?.inactive ?? 0} description={t('dashboard.kpi.pausedManually')}
                        icon={<FolderOff sx={{ fontSize: 18 }} />} accentColor="#F59E0B"
                        iconBg="rgba(245,158,11,0.1)" iconColor="#F59E0B"
                        valueColor={stats?.inactive ? "#F59E0B" : '#0F172A'}
                    />
                    <KpiCard
                        label={t('dashboard.kpi.actionsToday')} value={stats?.actionsToday ?? 0} description={t('dashboard.kpi.filesOrganized')}
                        icon={<TrendingUp sx={{ fontSize: 18 }} />} accentColor="#0EA5E9"
                        iconBg="rgba(14,165,233,0.1)" iconColor="#0EA5E9"
                    />
                    <KpiCard
                        label={t('dashboard.kpi.errorsToday')} value={errorsToday} description={t('dashboard.kpi.errorsInService')}
                        icon={<ErrorOutline sx={{ fontSize: 18 }} />}
                        accentColor={errorsToday > 0 ? "#EF4444" : "#10B981"}
                        iconBg={errorsToday > 0 ? "rgba(239,68,68,0.1)" : "rgba(16,185,129,0.1)"}
                        iconColor={errorsToday > 0 ? "#EF4444" : "#10B981"}
                        valueColor={errorsToday > 0 ? "#EF4444" : '#0F172A'}
                    />
                </Box>

                {/* Fila principal: columna izquierda + sidebar */}
                <Grid container spacing={2} columns={12}>

                    {/* Columna izquierda: reglas + actividad reciente */}
                    <Grid size={{ xs: 12, md: 8 }}>
                        <Stack spacing={2}>

                            {/* Últimas reglas */}
                            <Card sx={{ border: '1px solid #E2E8F0', boxShadow: 'none' }}>
                                <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
                                        <Typography variant="body1" fontWeight={600} sx={{ color: '#0F172A' }}>
                                            {t('dashboard.latestRules')}
                                        </Typography>
                                        <Button variant="text" size="small" endIcon={<ArrowForward sx={{ fontSize: 13 }} />}
                                            onClick={() => navigate(routes.rules.path)}
                                            sx={{ color: '#2563EB', fontWeight: 600, fontSize: '0.75rem' }}>
                                            {t('dashboard.viewAll')}
                                        </Button>
                                    </Stack>

                                    {latest.length === 0 ? (
                                        <Box sx={{ py: 4, textAlign: 'center' }}>
                                            <FolderSpecial sx={{ fontSize: 40, color: '#CBD5E1', mb: 1.5 }} />
                                            <Typography variant="body2" fontWeight={500} sx={{ color: '#64748B', mb: 0.5 }}>
                                                {t('dashboard.noRules')}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: '#94A3B8', display: 'block', mb: 2 }}>
                                                {t('dashboard.noRulesDesc')}
                                            </Typography>
                                            <Button variant="contained" startIcon={<Add />} onClick={() => navigate(routes.newRule.path)}
                                                sx={{ bgcolor: '#2563EB', '&:hover': { bgcolor: '#1D4ED8' } }}>
                                                {t('dashboard.createFirstRule')}
                                            </Button>
                                        </Box>
                                    ) : (
                                        <Stack divider={<Divider flexItem sx={{ borderColor: '#F1F5F9' }} />} spacing={0}>
                                            {latest.map((r) => {
                                                const active = statusIsActive((r as any).status, r.enabled);
                                                const lastRun = lastRunByRuleId.get(r.id ?? '');
                                                return (
                                                    <Stack key={r.id} direction="row" spacing={2} alignItems="center"
                                                        sx={{ py: 1.25, px: 0.5, '&:hover': { bgcolor: '#F8FAFC' }, borderRadius: 1, cursor: 'pointer' }}
                                                        onClick={() => navigate(routes.editRule.to({ id: r.id! }))}>
                                                        <Tooltip title={active ? t('dashboard.ruleActive') : t('dashboard.ruleInactive')}>
                                                            <Box sx={{ width: 7, height: 7, borderRadius: '50%', flexShrink: 0, bgcolor: active ? '#10B981' : '#CBD5E1' }} />
                                                        </Tooltip>
                                                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                                            <Typography variant="body2" fontWeight={500} sx={{ color: '#1E293B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                {r.name}
                                                            </Typography>
                                                        </Box>
                                                        <Chip label={active ? t('dashboard.ruleActive') : t('dashboard.ruleInactive')} size="small"
                                                            sx={{ bgcolor: active ? 'rgba(16,185,129,0.1)' : 'rgba(203,213,225,0.3)', color: active ? '#10B981' : '#94A3B8', fontWeight: 600, border: 'none', flexShrink: 0, height: 22, fontSize: '0.72rem' }} />
                                                        <Tooltip title={lastRun ? `${formatDate(lastRun, locale)} ${formatTime(lastRun, locale)}` : ''}>
                                                            <Typography variant="caption" sx={{ color: lastRun ? '#64748B' : '#CBD5E1', flexShrink: 0, minWidth: 80, textAlign: 'right', fontSize: '0.72rem' }}>
                                                                {lastRun ? formatRelativeTime(lastRun) : t('dashboard.noRuns')}
                                                            </Typography>
                                                        </Tooltip>
                                                        <ArrowForward sx={{ fontSize: 13, color: '#CBD5E1', flexShrink: 0 }} />
                                                    </Stack>
                                                );
                                            })}
                                        </Stack>
                                    )}
                                </CardContent>
                            </Card>

                            {/* Actividad reciente */}
                            {recentActivity.length > 0 && (
                                <Card sx={{ border: '1px solid #E2E8F0', boxShadow: 'none' }}>
                                    <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                                        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
                                            <Typography variant="body1" fontWeight={600} sx={{ color: '#0F172A' }}>
                                                {t('dashboard.recentActivity')}
                                            </Typography>
                                            <Button variant="text" size="small" onClick={() => navigate(routes.history.path)}
                                                sx={{ color: '#2563EB', fontWeight: 600, fontSize: '0.75rem', minWidth: 0, p: 0 }}>
                                                {t('dashboard.viewAllActivity')}
                                            </Button>
                                        </Stack>
                                        <Stack divider={<Divider flexItem sx={{ borderColor: '#F1F5F9' }} />} spacing={0}>
                                            {recentActivity.map((record) => {
                                                const actionKey = record.actionType ?? '';
                                                const actionStyle = ACTION_TYPE_COLORS[actionKey] ?? { bg: 'rgba(100,116,139,0.1)', color: '#64748B' };
                                                const actionLabel = t(`dashboard.actions.${actionKey}` as any, { defaultValue: actionKey });
                                                return (
                                                    <Stack key={record.id} direction="row" alignItems="center" spacing={2} sx={{ py: 1.25, px: 0.5 }}>
                                                        <Box sx={{ bgcolor: actionStyle.bg, color: actionStyle.color, px: 0.8, py: 0.3, borderRadius: 0.75, flexShrink: 0, minWidth: 52, textAlign: 'center' }}>
                                                            <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, lineHeight: 1 }}>{actionLabel}</Typography>
                                                        </Box>
                                                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                                            <Typography variant="body2" fontWeight={500} sx={{ color: '#1E293B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                {record.fileName ?? '—'}
                                                            </Typography>
                                                            {record.ruleName && (
                                                                <Typography sx={{ fontSize: '0.72rem', color: '#94A3B8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                    {record.ruleName}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                        <Typography variant="caption" sx={{ color: '#94A3B8', flexShrink: 0, fontSize: '0.72rem' }}>
                                                            {formatRelativeTime(record.timestamp)}
                                                        </Typography>
                                                    </Stack>
                                                );
                                            })}
                                        </Stack>
                                    </CardContent>
                                </Card>
                            )}

                        </Stack>
                    </Grid>

                    {/* Sidebar: acciones rápidas + estado del servicio */}
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Stack spacing={2} sx={{ height: '100%' }}>

                            {/* Acciones rápidas */}
                            <Card sx={{ border: '1px solid #E2E8F0', boxShadow: 'none' }}>
                                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                    <Typography variant="body1" fontWeight={600} sx={{ color: '#0F172A', mb: 1.5 }}>
                                        {t('dashboard.quickActions')}
                                    </Typography>
                                    <Stack spacing={1}>
                                        {[
                                            { labelKey: 'dashboard.newRule', icon: <Add sx={{ fontSize: 18 }} />, color: '#2563EB', bg: 'rgba(37,99,235,0.06)', path: routes.newRule.path },
                                            { labelKey: 'dashboard.viewHistory', icon: <History sx={{ fontSize: 18 }} />, color: '#0EA5E9', bg: 'rgba(14,165,233,0.06)', path: routes.history.path },
                                            { labelKey: 'dashboard.viewLogs', icon: <Article sx={{ fontSize: 18 }} />, color: '#8B5CF6', bg: 'rgba(139,92,246,0.06)', path: routes.logs.path },
                                            { labelKey: 'dashboard.settings', icon: <Settings sx={{ fontSize: 18 }} />, color: '#64748B', bg: 'rgba(100,116,139,0.06)', path: routes.settings.path },
                                        ].map(({ labelKey, icon, color, bg, path }) => (
                                            <Button key={labelKey} fullWidth onClick={() => navigate(path)} startIcon={icon}
                                                sx={{ justifyContent: 'flex-start', color, bgcolor: bg, '&:hover': { bgcolor: bg, filter: 'brightness(0.95)' }, py: 1.25, px: 1.5, borderRadius: 1.5, fontWeight: 600, fontSize: '0.875rem', textTransform: 'none' }}>
                                                {t(labelKey as any)}
                                            </Button>
                                        ))}
                                    </Stack>
                                </CardContent>
                            </Card>

                            {/* Estado del servicio */}
                            <Card sx={{ border: '1px solid #E2E8F0', boxShadow: 'none' }}>
                                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                                    <Typography variant="body2" fontWeight={600} sx={{ color: '#0F172A', mb: 1 }}>
                                        {t('dashboard.serviceStatus')}
                                    </Typography>
                                    <Stack spacing={0.5} divider={<Divider sx={{ borderColor: '#F1F5F9' }} />}>
                                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                                            <Typography variant="caption" sx={{ color: '#64748B' }}>{t('dashboard.connection')}</Typography>
                                            <Chip label={t('dashboard.connected')} size="small" sx={{ bgcolor: 'rgba(16,185,129,0.1)', color: '#10B981', fontWeight: 600, height: 20, fontSize: '0.7rem' }} />
                                        </Stack>
                                        {settings?.watchFolder && (
                                            <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
                                                <Typography variant="caption" sx={{ color: '#64748B', flexShrink: 0 }}>{t('dashboard.watchFolder')}</Typography>
                                                <Tooltip title={settings.watchFolder}>
                                                    <Typography variant="caption" fontWeight={500} sx={{ color: '#1E293B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 110, textAlign: 'right' }}>
                                                        {settings.watchFolder.split(/[\\/]/).pop() || settings.watchFolder}
                                                    </Typography>
                                                </Tooltip>
                                            </Stack>
                                        )}
                                        {lastExecution && (
                                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                <Typography variant="caption" sx={{ color: '#64748B' }}>{t('dashboard.lastExecution')}</Typography>
                                                <Tooltip title={`${formatDate(lastExecution, locale)} ${formatTime(lastExecution, locale)}`}>
                                                    <Typography variant="caption" sx={{ color: '#64748B' }}>
                                                        {formatRelativeTime(lastExecution)}
                                                    </Typography>
                                                </Tooltip>
                                            </Stack>
                                        )}
                                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                                            <Typography variant="caption" sx={{ color: '#64748B' }}>{t('dashboard.coverage')}</Typography>
                                            <Typography variant="caption" fontWeight={600} sx={{ color: '#0F172A' }}>
                                                {t('dashboard.pctActive', { pct: activePct })}
                                            </Typography>
                                        </Stack>
                                        {data?.generatedAtUtc && (
                                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                <Typography variant="caption" sx={{ color: '#64748B' }}>{t('dashboard.updated')}</Typography>
                                                <Typography variant="caption" sx={{ color: '#94A3B8' }}>
                                                    {formatTime(data.generatedAtUtc, locale)}
                                                </Typography>
                                            </Stack>
                                        )}
                                    </Stack>
                                </CardContent>
                            </Card>

                        </Stack>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
}
